import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-comprehensive-biomarker-panel"),
  kind: "test",
  tags: [
    "testing",
    "both-partners",
    "assessment",
    "metabolic",
    "autoimmune",
    "nutritional",
    "pre-cycle",
  ],
  title: "Comprehensive Biomarker Panel",
  subtitle:
    "100+ blood markers across fertility, hormones, thyroid, metabolic health, inflammation, nutrients, and pregnancy risk",
  icon: "🩸",
  body: {
    testName: "Comprehensive Fertility Biomarker Panel",
    category: "blood-marker",
    whatItMeasures:
      "A broad blood panel that looks beyond the usual fertility baseline. It can include reproductive hormones, thyroid markers, metabolic markers, advanced lipids, inflammation markers, nutrient status, liver and kidney function, CBC, autoimmune screening, infectious disease screening, and selected pregnancy-risk markers.\n\nThe goal is not to create a pile of disconnected lab values. The goal is to find correctable issues before treatment or pregnancy and to give your clinician a baseline that connects fertility, metabolic health, inflammation, nutrition, and pregnancy safety.",
    howItWorks:
      "Most markers are collected in a single fasting morning blood draw through a lab, clinic, or mobile phlebotomy partner. Some fertility-specific markers need separate timing. Progesterone, for example, is usually drawn about 7 days after ovulation when the goal is to confirm ovulation. Results are then reviewed together rather than one marker at a time, because patterns matter.",
    howItWorksSteps: [
      "Plan timing. Most markers can be drawn fasting in the morning, but cycle-specific markers like progesterone need separate timing.",
      "Complete the main blood draw. The panel can include hormones, thyroid, metabolic markers, lipids, inflammation, nutrients, liver, kidney, CBC, autoimmune, infectious disease, and selected pregnancy-risk labs.",
      "Add cycle-specific or history-specific markers when relevant. Examples include mid-luteal progesterone, antiphospholipid antibodies for recurrent loss context, or thrombophilia genetics in selected patients.",
      "Review the results as a pattern. A fertility-focused review connects markers like insulin, TSH, vitamin D, ferritin, inflammation, and prolactin to the reproductive plan.",
      "Retest what can change. Actionable markers such as fasting insulin, HbA1c, TSH, vitamin D, ferritin, homocysteine, and prolactin can be followed after treatment or supplementation.",
    ],
    quickFacts: [
      { kind: "sample", value: "Blood" },
      { kind: "time", value: "One main draw, plus timed add-ons" },
      { kind: "pain", value: "Needle pinch" },
      { kind: "prep", value: "Fasting morning draw for most markers" },
      { kind: "results", value: "Often 1-2 weeks" },
    ],
    markerTiming: [
      {
        marker: "Main biomarker panel",
        timing: "Fasting morning draw",
        reason:
          "Fasting and morning timing make metabolic, lipid, insulin, cortisol, and some hormone values easier to interpret consistently.",
      },
      {
        marker: "Progesterone",
        timing: "About 7 days after ovulation when checking ovulation",
        reason:
          "Progesterone rises after ovulation, so a random cycle-day result may miss the useful window.",
      },
      {
        marker: "Antiphospholipid antibodies",
        timing: "Any day; repeat about 12 weeks later if positive",
        reason:
          "A persistent positive result matters more than a single isolated result when evaluating antiphospholipid syndrome.",
      },
      {
        marker: "TSH, vitamin D, ferritin, insulin, HbA1c",
        timing: "Baseline, then retest after treatment interval",
        reason:
          "These are actionable markers. Retesting is usually timed to the biology of the intervention, often 6-12 weeks or around 3 months depending on the marker.",
      },
    ],
    whyItMatters: [
      "Fasting insulin and HbA1c can reveal insulin resistance or glucose risk that may affect ovulation, PMOS (ex PCOS), pregnancy risk, and egg-quality planning.",
      "TSH, thyroid antibodies, prolactin, and vitamin D are common, actionable findings in fertility workups.",
      "Ferritin, B12, folate, homocysteine, zinc, magnesium, and omega-3 status can show correctable nutritional gaps before pregnancy.",
      "hs-CRP, ESR, autoimmune markers, and antiphospholipid antibodies can add context when there is recurrent loss, implantation failure, autoimmune history, or inflammatory concern.",
      "ApoB, lipoprotein(a), LDL particle measures, kidney function, liver function, and CBC provide pregnancy-readiness and long-term health context.",
    ],
    expectations: {
      canExpect: [
        "A much broader baseline than a standard fertility hormone panel",
        "Actionable findings that can be optimized before treatment or pregnancy",
        "A way to track whether interventions are moving the markers that can actually change",
      ],
      shouldNotExpect: [
        "Every abnormal marker to be a fertility diagnosis by itself",
        "All markers to need retesting at the same interval",
        "Genetic or antibody results to change over time like vitamin D, TSH, ferritin, or insulin can",
      ],
    },
    considerations: [
      "The exact marker list should be transparent because different commercial panels use different definitions of 'comprehensive.'",
      "Not every marker is equally actionable. The value comes from interpretation and prioritization, not simply ordering more labs.",
      "Medication, supplements, fasting status, cycle timing, pregnancy status, and recent illness can all change interpretation.",
      "If a marker is being used to guide treatment, ask when it should be retested and what decision would change based on the result.",
    ],
    turnaroundTime: "Often 1-2 weeks",
    atHome: false,
    clinicRequired: false,
    dataPoints: [
      makeId("dp-fsh-level"),
      makeId("dp-lh-level"),
      makeId("dp-estradiol-level"),
      makeId("dp-progesterone-level"),
      makeId("dp-prolactin-level"),
      makeId("dp-testosterone-level"),
      makeId("dp-dhea-s-level"),
      makeId("dp-tsh-level"),
      makeId("dp-free-t4-level"),
      makeId("dp-free-t3-level"),
      makeId("dp-fasting-insulin"),
      makeId("dp-hba1c"),
      makeId("dp-hs-crp"),
      makeId("dp-homocysteine"),
      makeId("dp-vitamin-d-level"),
      makeId("dp-ferritin"),
      makeId("dp-apob"),
      makeId("dp-antiphospholipid-antibodies"),
      makeId("dp-thrombophilia-genetics"),
    ],
    section: "ovarian-reserve",
  },
  sources: [
    {
      title:
        "Diagnostic evaluation of the infertile female: a committee opinion",
      journal: "Fertility and Sterility",
      year: 2021,
      evidenceLevel: "expert-opinion",
    },
    {
      title: "Insulin resistance and reproductive outcomes in PMOS (ex PCOS)",
      journal: "Human Reproduction Update",
      year: 2021,
      evidenceLevel: "review",
    },
    {
      title:
        "Vitamin D and IVF outcomes: a systematic review and meta-analysis",
      journal: "Human Reproduction",
      year: 2022,
      evidenceLevel: "meta-analysis",
    },
  ],
  readTimeMinutes: 6,
};

registerBlock(test);
